import { useEffect, useState } from 'react';
import { formatarDataHora, type ConviteParaAprovarDto } from '@nexital/shared';
import { useDependencias, useEnvio } from '../../application/dependencias';
import { Dialogo } from '../design-system/Dialogo';

const SITUACOES: Record<ConviteParaAprovarDto['situacao'], string> = {
  aguardando: 'Aguardando a sua aprovação',
  aprovado: 'Já aprovado',
  recusado: 'Recusado',
  vencido: 'Venceu sem aprovação',
};

function Dados({ c }: { c: ConviteParaAprovarDto }) {
  return (
    <dl className="dialogo__ficha">
      <div>
        <dt>Convidado</dt>
        <dd>{c.email}</dd>
      </div>
      <div>
        <dt>Perfil</dt>
        <dd>{c.perfil}</dd>
      </div>
      <div>
        <dt>Pedido por</dt>
        <dd>{c.convidadoPor}</dd>
      </div>
      {c.gestor && (
        <div>
          <dt>Gestor direto</dt>
          <dd>{c.gestor}</dd>
        </div>
      )}
      <div>
        <dt>Pedido em</dt>
        <dd>{formatarDataHora(c.criadoEm)}</dd>
      </div>
      <div>
        <dt>Aprovar até</dt>
        <dd>{formatarDataHora(c.aprovarAte)}</dd>
      </div>
      <div>
        <dt>Situação</dt>
        <dd>
          {SITUACOES[c.situacao]}
          {c.motivoRecusa && ` · ${c.motivoRecusa}`}
        </dd>
      </div>
    </dl>
  );
}

function Recusa({ motivo, definir, enviando, recusar }: { motivo: string; definir: (m: string) => void; enviando: boolean; recusar: () => void }) {
  return (
    <div className="dialogo__recusa">
      <label className="campo">
        <span>Motivo da recusa (opcional)</span>
        <input value={motivo} maxLength={500} onChange={(e) => definir(e.target.value)} />
      </label>
      <button type="button" className="botao botao--perigo" disabled={enviando} onClick={recusar}>
        Recusar
      </button>
    </div>
  );
}

/** Aprovação do convite pedido por um colaborador (ADR-0021): os dados do convite, aprovar (envia) ou recusar com motivo. */
export function DialogoAprovarConvite({ uuid }: { uuid: string }) {
  const { espacos } = useDependencias();
  const fechar = useEnvio((s) => s.abrirConvite);
  const avisar = useEnvio((s) => s.avisar);
  const [convite, definir] = useState<ConviteParaAprovarDto | null>(null);
  const [motivo, definirMotivo] = useState('');
  const [enviando, definirEnviando] = useState(false);
  const [erro, definirErro] = useState<string>();

  useEffect(() => {
    espacos.conviteParaAprovar(uuid).then(definir, (e: Error) => definirErro(e.message));
  }, [espacos, uuid]);

  const decidir = async (decisao: 'aprovar' | 'recusar') => {
    definirEnviando(true);
    try {
      await espacos.decidirConvite(uuid, decisao, motivo.trim() || undefined);
      avisar(decisao === 'aprovar' ? `Convite para ${convite?.email} aprovado e enviado.` : `Convite para ${convite?.email} recusado.`);
      fechar(null);
    } catch (e) {
      definirErro((e as Error).message);
      definirEnviando(false);
    }
  };
  const pode = Boolean(convite?.podeDecidir && convite.situacao === 'aguardando');

  return (
    <Dialogo
      titulo="Convite aguardando aprovação"
      descricao={pode ? 'Aprovado, o convite é enviado na hora para o e-mail abaixo.' : undefined}
      rotuloConfirmar={enviando ? 'Enviando…' : 'Aprovar e enviar'}
      podeConfirmar={pode && !enviando}
      aoConfirmar={() => void decidir('aprovar')}
      aoCancelar={() => fechar(null)}
    >
      {convite ? <Dados c={convite} /> : !erro && <p>Carregando…</p>}
      {pode && <Recusa motivo={motivo} definir={definirMotivo} enviando={enviando} recusar={() => void decidir('recusar')} />}
      {erro && (
        <p className="cartao-acesso__erro" role="alert">
          {erro}
        </p>
      )}
    </Dialogo>
  );
}
